import { useState } from 'react';
import { Shield, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface AuthScreenProps {
  initialMode?: 'login' | 'signup';
  onClose?: () => void;
  onSuccess?: () => void;
}

const getSupabaseDebugHost = () => {
  const configuredUrl = import.meta.env.VITE_SUPABASE_URL;

  if (!configuredUrl) return 'missing VITE_SUPABASE_URL';

  try {
    return new URL(configuredUrl).host;
  } catch {
    return configuredUrl;
  }
};

const AuthScreen = (props: AuthScreenProps) => {
  const { initialMode = 'login', onClose, onSuccess } = props;
  const { signIn, signUp } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setMessage('');

    if (mode === 'login') {
      const result = await signIn(email, password);
      if (result.error) setError(result.error);
      else {
        onSuccess?.();
        onClose?.();
      }
    } else {
      const result = await signUp(email, password);
      if (result.error) setError(result.error);
      else if (result.needsEmailConfirmation) setMessage('Check your email to confirm your account, then log in.');
      else {
        setMessage('Account created. You are now logged in.');
        onSuccess?.();
        onClose?.();
      }
    }

    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950/80 backdrop-blur-sm text-slate-100 flex items-center justify-center p-6">
      <div className="relative w-full max-w-md rounded-[2rem] border border-slate-800 bg-slate-900/95 p-8 shadow-2xl shadow-black/30">
        {onClose ? (
          <button
            onClick={onClose}
            className="absolute right-4 top-4 rounded-xl p-2 text-slate-500 transition-colors hover:bg-slate-800 hover:text-slate-200"
            aria-label="Close auth dialog"
          >
            <X size={16} />
          </button>
        ) : null}
        <div className="flex items-center gap-3">
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-emerald-300">
            <Shield size={18} />
          </div>
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-400">Supabase Auth</p>
            <h1 className="mt-1 text-2xl font-black text-white">{mode === 'login' ? 'Log In' : 'Create Account'}</h1>
          </div>
        </div>

        <p className="mt-4 text-sm text-slate-400">
          {mode === 'login'
            ? 'Log in with email and password to access the app.'
            : 'Create a simple email/password account. Nothing else is required yet.'}
        </p>

        {import.meta.env.DEV ? (
          <p className="mt-3 rounded-2xl border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
            Debug target: {getSupabaseDebugHost()}
          </p>
        ) : null}

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e: any) => setEmail((e.target as HTMLInputElement).value)}
              className="w-full rounded-[1.25rem] border border-slate-700 bg-slate-950/80 px-4 py-3 text-sm text-slate-200 outline-none transition-colors focus:border-emerald-500"
              placeholder="you@example.com"
              required
            />
          </div>

          <div>
            <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e: any) => setPassword((e.target as HTMLInputElement).value)}
              className="w-full rounded-[1.25rem] border border-slate-700 bg-slate-950/80 px-4 py-3 text-sm text-slate-200 outline-none transition-colors focus:border-emerald-500"
              placeholder="Minimum 6 characters"
              minLength={6}
              required
            />
          </div>

          {error ? <p className="text-sm text-rose-300">{error}</p> : null}
          {message ? <p className="text-sm text-emerald-300">{message}</p> : null}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-[1.25rem] bg-emerald-600 px-4 py-3 text-sm font-black text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? 'Working...' : mode === 'login' ? 'Log In' : 'Sign Up'}
          </button>
        </form>

        <button
          onClick={() => {
            setMode((current: 'login' | 'signup') => (current === 'login' ? 'signup' : 'login'));
            setError('');
            setMessage('');
          }}
          className="mt-4 w-full text-sm text-slate-400 transition-colors hover:text-slate-200"
        >
          {mode === 'login' ? 'Need an account? Sign up' : 'Already have an account? Log in'}
        </button>
      </div>
    </div>
  );
};

export default AuthScreen;
